/**
 * Organisations, departments and roles — the administration surface behind the access screen.
 *
 * ONE WORD FOR ONE THING: this says ORGANISATION throughout, never "tenant". The column is
 * `organisation_id`, the path is `/organisations`, and every other screen in the console already
 * says organisation. A screen that says Tenant over a model that says organisation makes the
 * reader translate, and translation is where mistakes are made.
 *
 * WHAT A PARENT CAN SEE, because it is the whole design and it is easy to widen by accident: a
 * parent organisation administers its children's PEOPLE and ROLES. It never sees their sites,
 * telemetry, alarms, findings or reports. Every endpoint here is scoped by the server through
 * `manageableOrgIds`; nothing in this file can widen anything, and nothing operational reads it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
/* The two vocabularies live in `app/theme.ts`, which is where `tokens.css` is read from and
   where the pickers are built. Imported rather than retyped: the backend already validates
   against the pg enums themselves for the same reason, and a fourth copy of four palette
   names is the one most likely to drift, because it is the one nothing renders. */
import type { Accent, Palette } from '@/app/theme';

/* ------------------------------------------------------------------- types */

/**
 * `inactive` arrived with 0046 and is NOT a synonym for `suspended`. Suspended is a decision
 * somebody took about this organisation; inactive is a state it drifted into. The screen keeps
 * them apart because the person reading it has a different next action for each.
 */
export type OrgStatus = 'active' | 'suspended' | 'inactive';

export interface Organisation {
  id: string;
  name: string;
  slug: string;
  status: OrgStatus;
  /** NULL for a root. The tree is built client-side from these. */
  parentId: string | null;
  /**
   * The parent's name — served because the parent is often outside the reader's reach (a tenant's
   * list stops at itself), and the drawer names who set its limits.
   */
  parentName: string | null;
  /**
   * Operates the deployment, so it may set its OWN limits (migration 0049).
   *
   * Every other organisation's caps are its parent's to set — "a cap you can raise yourself is
   * not a cap" — which left a ROOT unable to have its budget set by anybody, because a root has
   * no ancestor. That shipped sub-organisations unreachable. This flag is why the screen can
   * offer the control, and why a root WITHOUT it must be told that plainly rather than shown a
   * dead end.
   */
  isPlatform: boolean;
  /**
   * When this organisation was retired, or `null` while it is live (migration 0062).
   *
   * ALWAYS NULL UNLESS THE LIST ASKED FOR RETIRED ROWS. `GET /organisations` drops them by
   * default, so a screen that never passes `includeRetired` will never see a non-null value
   * here and does not have to handle one. The flag exists because a restore has to be findable:
   * a retired organisation is only meaningful beside the live siblings it sat among, so it comes
   * back in the same list rather than in a second one.
   */
  retiredAt: string | null;
  canCreateChildren: boolean;
  /** NULL = unlimited, once `canCreateChildren` is on. */
  childLimit: number | null;
  childCount: number;
  /** NULL = uncapped. Counts INTEGRATED devices, not discovered ones. */
  deviceCredits: number | null;
  deviceCreditsUsed: number;
  /** NULL = uncapped. Counts members whose access has not been revoked. */
  memberLimit: number | null;
  memberCount: number;
  roleCount: number;

  /* ---------------------------------------------------------------------------------------
   * RECORDED, NOT ENFORCED. Every field below is stored and shown and changes no behaviour.
   *
   * They are here because an administrator needs somewhere to keep who to call about this
   * organisation, and inventing a second place for it later is worse. The screen must LABEL
   * them as recorded — `otpRequired` above all, which reads as a security control and is not
   * one: nothing at sign-in consults it. A tick box that looks like it requires a second
   * factor and does not is worse than no tick box, so the modal says so in words.
   * --------------------------------------------------------------------------------------- */
  address: string | null;
  pocName: string | null;
  pocEmail: string | null;
  pocPhone: string | null;
  isTrial: boolean;
  /** ISO 8601, or NULL. Nothing expires when the date passes. */
  trialEndsAt: string | null;
  otpRequired: boolean;

  /**
   * ADR-0014's OUTERMOST GATE, and the one field in this block that IS enforced — which is why
   * it sits below the recorded ones rather than among them.
   *
   * While it is false `writes/service.ts` refuses all five entry points of the write path and no
   * permission overrides it, so `GET /me` AND-s `can.proposeWrites` with it and the console hides
   * the rail entirely. Nothing in the console could SET it until this release: the column arrived
   * with 0031 and the only control was `update organisations set remote_control_enabled = true`
   * at a psql prompt — the one switch deciding whether people may drive live plant was the one
   * switch nothing recorded the opening of, in a product whose output is evidence.
   *
   * SERVED ON EVERY ROW, not only the caller's own. `/me` reports YOUR organisation's gate; this
   * reports the gate of whichever organisation the row is, so an ancestor can see that a
   * descendant's is open. Any screen asking "is remote control on HERE" must read it from the row
   * it is describing — reading `/me` while the header points at a descendant answers about the
   * wrong organisation, quietly, and only for the callers who reach more than one.
   */
  remoteControlEnabled: boolean;

  /* ---------------------------------------------------------------------------------------
   * THE ORGANISATION'S LOOK — RAW, AND `null` HERE MEANS SOMETHING `/me` NEVER SAYS.
   *
   * Two of the console's three appearance axes belong to the organisation (0058): `palette` is
   * which neutral the surfaces are mixed from, `accent` is the brand hue. The third — light
   * versus dark — is personal, stays in the browser, and is deliberately not server state.
   *
   * `null` is "has never chosen", NOT `thunder`/`lime`. This endpoint serves the column as it
   * stands because this is the EDITOR's view, and an editor that showed `thunder` for an unset
   * palette could never offer a way back to unset. `GET /me` is the one reader that resolves the
   * null, server-side, once — see `MeResponse.organisation`, and do not add a second resolution
   * here. Two copies of a default drift in exactly one direction: the platform re-brands and
   * every tenant that never chose keeps rendering the old look.
   * --------------------------------------------------------------------------------------- */
  palette: Palette | null;
  accent: Accent | null;
}

/** The half of an organisation that records facts about it rather than bounding it. */
export interface OrganisationDetails {
  address?: string | null;
  pocName?: string | null;
  pocEmail?: string | null;
  pocPhone?: string | null;
  isTrial?: boolean;
  trialEndsAt?: string | null;
  otpRequired?: boolean;
}

export interface Department {
  id: string;
  organisationId: string;
  name: string;
  isDefault: boolean;
  /**
   * RECORDED, NOT ENFORCED, and the form says so.
   *
   * Suspending a department suspends nobody — departments deliberately carry no isolation, and a
   * second axis that could narrow what an operator sees is how a filter becomes a false
   * all-clear. Offered for parity with the reference product; labelled so nobody reads it as a
   * control.
   */
  status: 'active' | 'suspended' | 'inactive';
  memberCount: number;
}

/**
 * The role a following one reads its grants from — or would return to if it were reset.
 *
 * `organisationId` is NULL when the default is a platform TEMPLATE: a row belonging to no
 * organisation. That is NOT the same as "there is no default", which is the whole `follows`
 * object being null — the case of a role the tenant invented, where a reset would mean "resolve
 * to nothing" and the server refuses it by name. `organisationName` is null in exactly the same
 * case, so `role-follow.ts` turns the pair into one phrase and every sentence on the screen
 * reads from that.
 */
export interface RoleFollowSource {
  roleId: string;
  organisationId: string | null;
  organisationName: string | null;
}

export interface RoleDetail {
  id: string;
  slug: string;
  name: string;
  isSystem: boolean;
  /**
   * `organisation` — reaches every site the organisation owns — or `site`, meaning it reaches
   * only the sites assigned to whoever holds it.
   *
   * On the ROLE rather than on the membership because it answers a question about the role: is
   * this a kind of access that gets narrowed at all? WHICH sites is a property of the person and
   * lives on their grants. Putting the sites on the role would multiply roles by sites, so
   * changing what Engineer means would mean editing every copy.
   */
  scopeKind: 'organisation' | 'site';
  organisationId: string | null;
  /**
   * What the role MEANS today — resolved through the follow, then clipped to the organisation's
   * entitlement.
   *
   * For a following role these are the grants of the row it follows, which is why a permission
   * added to a template appears here for every follower with nothing re-run. `role_permissions`
   * is a VIEW over `role_permissions_own` doing the resolution (migration 0063), so this field
   * needed no new endpoint and every existing reader got propagation for free.
   */
  permissions: string[];
  /**
   * NULL while this role FOLLOWS its default; the moment somebody broke the link otherwise.
   *
   * It is what decides whether the permission picker is editable at all: a following role holds
   * no grants of its own, and `PATCH /roles/:id` with permissions is refused on one BY NAME
   * rather than customising it implicitly. See `role-follow.ts`, which owns that decision.
   */
  customisedAt: string | null;
  /** Reported whether or not the link is live — a following role has to name whose default it is
      showing, and a customised one has to know whether there is anything to reset TO. */
  follows: RoleFollowSource | null;
  memberCount: number;
}

export interface PermissionDef {
  slug: string;
  /** One of the four areas: The plant · Evidence · Remote control · People and access. */
  area: string;
  label: string;
  blurb: string;
}

/**
 * What an organisation is ENTITLED to hold, before any of its roles spend it.
 *
 * ── THE CEILING IS A DIFFERENT QUESTION FROM A ROLE, AND THE SCREEN MUST NOT MERGE THEM ──
 * A role says what a PERSON may do. A ceiling says what the ORGANISATION may hold at all — set
 * from above, by the platform operator, and intersected with every role beneath it on every
 * request (`role_permissions ∩ ceiling`, in `plugins/auth.ts`). So a tenant can tick a permission
 * into a role and still not hold it, if the ceiling above them does not include it.
 *
 * Three fields answer the three questions the form has, and all three come from the server rather
 * than being re-derived here — the alternative is a second copy of a rule whose whole purpose is
 * to be unforgeable:
 *
 *   `permissions`  what this organisation holds NOW — the ticked boxes.
 *   `grantable`    the CALLER'S own ceiling: the only boxes that may be drawn. You cannot give
 *                  away what you do not hold, so anything outside this is ABSENT from the form —
 *                  not disabled, not dimmed. A greyed row advertises a capability somebody has
 *                  decided not to sell, and the counter would read "of 23" to a tenant who has 12.
 *   `editable`     may this caller set it. True only for a STRICT ANCESTOR of a non-platform
 *                  organisation: `isStrictAncestor(homeOrgId, target) && !target.isPlatform`.
 *
 * `editable` is false on your own organisation, and that is the load-bearing case rather than a
 * courtesy. The Administrator seat can be neither vacated nor duplicated (migrations 0053/0055),
 * so an organisation that could untick `roles.manage` on itself would lock itself out
 * permanently, with nobody above it to undo it. The server refuses it; the form must not offer it.
 */
export interface Ceiling {
  organisationId: string;
  name: string;
  isPlatform: boolean;
  /** What this organisation holds now. */
  permissions: string[];
  /** The caller's own ceiling — the set the form may draw at all. */
  grantable: string[];
  /** Whether this caller may change it. False on your own organisation and on the platform's. */
  editable: boolean;
}

/* ------------------------------------------------------------------- keys */

export const tenancyKeys = {
  /*
   * The PREFIX every organisation query hangs off, and the only thing writes invalidate. Both
   * variants of the list and every ceiling sit under it, so one invalidation reaches all of
   * them and no caller has to remember which ones a write could have moved.
   */
  orgs: ['organisations'] as const,
  /** The list itself. Keyed by the flag, so the two answers cannot overwrite each other. */
  orgList: (includeRetired: boolean) => ['organisations', 'list', includeRetired] as const,
  departments: (orgId: string) => ['departments', orgId] as const,
  roles: (orgId: string) => ['roles', 'catalogue', orgId] as const,
  /**
   * The catalogue prefix — every per-organisation answer sits under it, so one invalidation of
   * `permissions` still clears them all (`app/providers.tsx` invalidates on an `x-lumen-access`
   * change and does not know which organisations are cached).
   */
  permissions: ['permissions'] as const,
  /** `null` = the caller's own. Keyed, so a tenant's catalogue and the reader's own coexist. */
  permissionsFor: (orgId: string | null) => ['permissions', orgId] as const,
  ceiling: (orgId: string) => ['organisations', orgId, 'ceiling'] as const,
};

/* ----------------------------------------------------------------- reads */

/**
 * Every organisation the caller may administer — their own plus descendants, flat.
 *
 * Flat with `parentId` rather than nested: the screen needs both a root list and a per-parent
 * child list, and one flat fetch answers both without the client asking twice or the server
 * shaping the tree two ways.
 */
export function useOrganisations(includeRetired = false) {
  return useQuery({
    queryKey: tenancyKeys.orgList(includeRetired),
    /*
     * The flag is sent only when it is ON. `includeRetired=false` and an absent parameter mean
     * the same thing to the server, and sending the word `false` would put a second spelling of
     * the default into every request for nothing.
     */
    queryFn: () =>
      api.get<{ organisations: Organisation[] }>(
        '/organisations',
        includeRetired ? { includeRetired: 'true' } : undefined,
      ),
    staleTime: 30_000,
  });
}

/*
 * ASKED IN THE ORGANISATION THEY DESCRIBE (`api.within`), not in the header's selection. The
 * server answers both only for the selection and what sits beneath it, so a form targeting a
 * parent or a sibling of the selection — the invite form's Organisation field — got a 404. The
 * answer depends only on the organisation asked about, so for one inside the selection nothing
 * changes.
 */
export function useDepartments(orgId: string | null) {
  return useQuery({
    queryKey: tenancyKeys.departments(orgId ?? 'none'),
    queryFn: () =>
      api.within(orgId ?? '').get<{ departments: Department[] }>('/departments', { orgId: orgId ?? '' }),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });
}

export function useRoleCatalogue(orgId: string | null) {
  return useQuery({
    queryKey: tenancyKeys.roles(orgId ?? 'none'),
    queryFn: () =>
      api.within(orgId ?? '').get<{ roles: RoleDetail[] }>('/roles/catalogue', { orgId: orgId ?? '' }),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });
}

/**
 * The permission catalogue — THIS ORGANISATION'S, not the platform's.
 *
 * ── IT IS NO LONGER IMMUTABLE, AND THAT MATTERS MORE THAN THE COUNT ──────────
 * This said "immutable per deployment — 14 rows that change only when the engine ships new
 * capabilities". Both halves have expired. There are 23 rows, and the server now serves the
 * intersection of the catalogue with the caller's organisation's PERMISSION CEILING: what the
 * platform operator has entitled that customer to hold at all. A permission outside it is ABSENT
 * here rather than listed as unavailable — which is the whole product rule, because a greyed row
 * advertises a capability somebody has decided not to sell. It is also what makes the "N of M"
 * counters on the Access screen read against this tenant's own entitlement rather than against
 * the platform's 23.
 *
 * So it can change without a deploy, in the middle of a session, because somebody unticked a box
 * one organisation above this one.
 *
 * ── AND `staleTime: Infinity` IS STILL RIGHT ────────────────────────────────
 * Not because the value cannot change, but because a CHANGE IS ANNOUNCED. `x-lumen-access` is a
 * digest of the permission set the server just enforced with, and the ceiling is folded into that
 * set on the server — so narrowing a tenant moves the digest on their very next response, and
 * `app/providers.tsx` invalidates the whole cache when it does. An explicit invalidation refetches
 * regardless of `staleTime`, so this query is corrected at the one moment being stale mattered.
 * A timer here would be a second mechanism answering a question the first one already answers,
 * and the two could disagree.
 */
/**
 * ── WHOSE CATALOGUE, AND WHY THE CALLER IS NOT ALWAYS THE ANSWER ────────────
 * `orgId` asks for THAT organisation's entitlement; omitting it asks for the caller's own. The
 * two are the same for a tenant administrator and differ for anybody administering somebody
 * else, which is what made this a bug: the platform operator's home holds every permission by
 * construction, so the Access screen drew a tenant's roles against 24 rows while the tenant was
 * entitled to 20, and the four extra were refused on save.
 *
 * Both surfaces are legitimate and they are different questions:
 *
 *   ROLE editing   → `usePermissionCatalogue(thatOrgId)`. What the organisation HAS. Offering
 *                    more is offering an act the API turns down.
 *   CEILING editing→ `usePermissionCatalogue()`. What the reader may GIVE. A form offering only
 *                    the 20 a tenant already holds could never tick the other 4 back on — a
 *                    one-way door, and the reason this parameter is optional rather than required.
 *
 * The key carries `orgId`, so the two live side by side in the cache instead of overwriting each
 * other — which they would have done under the old single key the moment both were on screen.
 */
export function usePermissionCatalogue(orgId?: string | null) {
  return useQuery({
    queryKey: tenancyKeys.permissionsFor(orgId ?? null),
    /*
     * ASKED IN THAT ORGANISATION, as `useRoleCatalogue` asks for its roles — not under whatever
     * the header selects. The two differ exactly when it matters: switching organisations resets
     * every query, and one still keyed to the organisation the screen was showing refetched under
     * the NEW selection, where that organisation is usually out of reach. Measured on the local
     * stack 2026-10-04: a burst of 404s on `/permissions?orgId=…` after every switch.
     */
    queryFn: () =>
      orgId
        ? api.within(orgId).get<{ permissions: PermissionDef[] }>('/permissions', { orgId })
        : api.get<{ permissions: PermissionDef[] }>('/permissions'),
    /* `enabled` is deliberately absent for the no-arg form: the caller's own catalogue is always
       askable. A null `orgId` means "mine", not "wait". */
    staleTime: Infinity,
  });
}

/**
 * One organisation's permission ceiling, and what the caller may do about it.
 *
 * Fetched per organisation rather than folded into `useOrganisations`, because `editable` and
 * `grantable` are answers about the CALLER's relationship to that row — they would have to be
 * recomputed for every organisation in the list to be carried there, and the form asks about
 * exactly one at a time.
 *
 * No `staleTime`: the app default of 30s applies, and a ceiling change moves `x-lumen-access` on
 * the next response anyway, which invalidates the whole cache (`app/providers.tsx`).
 */
export function useCeiling(orgId: string | null) {
  return useQuery({
    queryKey: tenancyKeys.ceiling(orgId ?? 'none'),
    queryFn: () => api.get<{ ceiling: Ceiling }>(`/organisations/${orgId ?? ''}/ceiling`),
    enabled: Boolean(orgId),
  });
}

/* ---------------------------------------------------------------- writes */

/**
 * Replace an organisation's ceiling.
 *
 * PUT AND NOT PATCH, because a ceiling is a SET and a partial update of a set is ambiguous in
 * exactly the direction that matters: "add these" and "these are all of them" differ by
 * everything the caller left out. The form renders every box it may offer and sends back the
 * ticked ones.
 *
 * TWO THINGS THE SERVER WILL REFUSE THAT THIS CANNOT PREVENT, so the caller must surface the
 * error rather than assume success:
 *   · setting your OWN organisation's ceiling — see `Ceiling.editable`;
 *   · GRANTING what the organisation ABOVE the target does not itself hold. A schema trigger
 *     refuses that outright — a ceiling can only shrink going down.
 *
 * NARROWING is not in that list, and until 0059 it was: a parent could not drop what a descendant
 * still held. It can now, and nothing is destroyed doing it — the descendant keeps its rows and
 * stops RESOLVING them, and re-widening the parent brings them back without visiting the child.
 *
 * Invalidates the organisation list too: a ceiling bounds what its roles can spend, so the counts
 * drawn beside them are downstream of this.
 */
export function useSetCeiling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: string[] }) =>
      api.put<{ ceiling: Ceiling }>(`/organisations/${id}/ceiling`, { permissions }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: tenancyKeys.ceiling(v.id) });
      void qc.invalidateQueries({ queryKey: tenancyKeys.orgs });
      void qc.invalidateQueries({ queryKey: tenancyKeys.roles(v.id) });
    },
  });
}

/**
 * Retire an organisation — the reversible end of a tenant, and the one an operator should reach
 * for (migration 0062).
 *
 * ── WHAT IT IS NOT ──────────────────────────────────────────────────────────
 * Not `DELETE /organisations/:id`, which is a hard delete the database refuses for almost every
 * organisation that has ever been used — it has sites, or people, or an audit trail that is
 * append-only by trigger. Retiring touches one column. Nothing is removed, no id is freed, every
 * audit row still resolves, and `restore` puts back exactly what was there.
 *
 * ── WHY A POST AND NOT A PATCH ──────────────────────────────────────────────
 * Ending a tenant's access must not be able to travel in the same request as a rename and a
 * phone number. A named act has a named audit row and a named refusal; a nullable column on the
 * patch body has neither, and would be one line away from being set by a form nobody read.
 *
 * ── WHAT IT REFUSES, SO THE SCREEN CAN SAY IT FIRST ─────────────────────────
 * The organisation you are signed in to (nobody inside it could restore it), the platform
 * organisation, and any organisation that still has a LIVE sub-organisation — retiring a parent
 * deliberately does not retire its children, because a restore could not then tell which of them
 * you had already retired yourself. All three come back as a sentence naming the remedy.
 */
export function useRetireOrganisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ organisation: Organisation }>(`/organisations/${id}/retire`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenancyKeys.orgs });
      /*
       * `/me` CARRIES THE HEADER'S ORGANISATION LIST (`app/org-context.tsx`), and a retired
       * organisation is no longer an operating context — so without this the switcher goes on
       * offering one that every request would now refuse.
       */
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/**
 * Put a retired organisation back, exactly as it was.
 *
 * Refuses only when its PARENT is retired, and says so by name: a live organisation under a
 * retired one is a branch nobody above it can administer, so the restore has to walk down from
 * the top rather than up from the leaf.
 */
export function useRestoreOrganisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ organisation: Organisation }>(`/organisations/${id}/restore`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenancyKeys.orgs });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useCreateOrganisation() {
  const qc = useQueryClient();
  return useMutation({
    /*
     * NO LIMITS HERE, and that is the server's design rather than an omission. A child is
     * created under the CALLER'S organisation with zero device credits if the caller is capped,
     * and `canCreateChildren` false — then the parent allocates deliberately through PATCH.
     * Create, then allocate: the allocation arithmetic lives in one place and has one entry.
     */
    mutationFn: (
      body: {
        name: string;
        /**
         * Create a TOP-LEVEL organisation instead of a child of your own.
         *
         * Refused by the server unless your organisation operates the deployment
         * (`isPlatform`). Provisioning tenants is a platform act, and a root has no parent —
         * so none of the guards that make a child safe apply to one.
         */
        asRoot?: boolean;
        /*
         * The limits the new organisation starts with — the same four the edit form carries.
         * Omitted means the old defaults (uncapped for a root, zero credits under a capped
         * parent, unable to sub-divide). A number is checked by the SAME allocation arithmetic
         * the PATCH uses, inside the insert's transaction, so a refusal leaves nothing behind.
         */
        deviceCredits?: number | null;
        memberLimit?: number | null;
        canCreateChildren?: boolean;
        childLimit?: number | null;
        /**
         * The new organisation's PERMISSION CEILING, as slugs.
         *
         * OMITTING IT INHERITS THE CREATOR'S WHOLE CEILING — which is what every organisation
         * created before this field existed got, and what migration 0056 grandfathered them to.
         * A narrower set is the deliberate act, and the default must not silently provision a
         * customer who can do nothing: an organisation whose administrator holds no permissions
         * looks exactly like a broken deployment.
         *
         * So the form sends this only when the reader actually narrowed it. Sending the full
         * grantable set would be identical in effect and would record an intent nobody had.
         */
        permissions?: string[];
      } & OrganisationDetails,
    ) => api.post<{ organisation: Organisation }>('/organisations', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: tenancyKeys.orgs }),
  });
}

/**
 * Edit an organisation.
 *
 * AN ORGANISATION CANNOT CHANGE ITS OWN LIMITS OR STATUS. The server refuses the NINE entitlement
 * fields — `status`, `deviceCredits`, `childLimit`, `memberLimit`, `canCreateChildren`, the trial
 * pair, `otpRequired` and `remoteControlEnabled` — when the target is the caller's own
 * organisation, because a cap you can raise on yourself is not a cap and a gate you can open
 * yourself is not a gate. `name` and the four CONTACT fields are neither, and are always editable.
 *
 * The consequence the screen has to show rather than discover: a ROOT organisation has no
 * ancestor, so its limits cannot be changed through this API at all. That is deliberate — a
 * root's budget is decided in SQL with a person attached, not by a button its own tenant holds.
 */
export function usePatchOrganisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      status?: OrgStatus;
      deviceCredits?: number | null;
      memberLimit?: number | null;
      childLimit?: number | null;
      canCreateChildren?: boolean;
      /**
       * Whether anybody in this organisation may drive live hardware at all (ADR-0014).
       *
       * IT IS NOT A CAP AND IT IS SENT LIKE ONE, because the server guards it with the caps: it
       * is a safety entitlement granted from above, and an organisation switching on its OWN
       * ability to write to a contactor is exactly what "no permission overrides it" exists to
       * prevent. So the same rule applies — a STRICT ANCESTOR only, plus the platform
       * organisation about its own row (0049's exception, which has to reach this field or the
       * deployment operator, being a root, would be the one organisation whose gate nobody could
       * ever open).
       *
       * Turning it ON grants nobody a write. It makes the write path answerable at all; the
       * two-person rule, the allowlist and `writes.propose`/`writes.approve` are all still in
       * front of every request. The form has to say that, because "enable remote control" reads
       * like the switch that starts closing breakers.
       */
      remoteControlEnabled?: boolean;
      /*
       * `null` is a real value and not an omission: it is how "follow the platform default
       * again" is said. `undefined` leaves the column alone. The server distinguishes the two
       * (`input.palette !== undefined`), so an optional-nullable pair here is the shape that
       * can express both, and a caller that flattens them loses the un-brand.
       *
       * Only a STRICT ANCESTOR of the target may send either — plus the platform organisation
       * about its own row (0049's exception). The server enforces it; `AccessView` draws the tab
       * only when the same condition holds, so the control is absent rather than refused.
       */
      palette?: Palette | null;
      accent?: Accent | null;
    } & OrganisationDetails) =>
      api.patch<{ organisation: Organisation }>(`/organisations/${id}`, body),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: tenancyKeys.orgs });
      /*
       * `/me` TOO, BUT ONLY WHEN THE LOOK MOVED.
       *
       * `GET /me` carries the RESOLVED palette and accent, and reading it is what paints the
       * console (`usePaintOrganisation`). Branding your OWN organisation — which the platform
       * operator may, 0049's exception — therefore changes a value `/me` holds, and without this
       * the console keeps the old look until the query goes stale five minutes later or the tab
       * is re-focused. The operator presses Save and nothing happens, which reads as a refused
       * write rather than a cached read.
       *
       * Conditional rather than unconditional: branding a TENANT does not change the caller's own
       * `/me` at all, and neither does renaming an organisation or moving a device credit. An
       * identity refetch on every one of those is a request asked for nothing.
       *
       * ── AND THE WRITE GATE, FOR THE SAME REASON ONE TIER FURTHER ALONG ──────
       *
       * `GET /me` carries `organisation.remoteControlEnabled` AND `can.proposeWrites`, which is
       * that flag AND-ed with the permission — and `app/nav.ts` hides the Remote control rail
       * entry outright while it is false. The only caller who may set it on their OWN row is the
       * platform operator (0049's exception), and for them the rail is exactly what moves: press
       * Save, and without this the entry does not appear until the query goes stale or the tab is
       * re-focused, which reads as a refused write rather than a cached read.
       *
       * Keyed on the FIELD and not on which organisation, so there is one rule here rather than a
       * second copy of "is this my own row" that would have to be kept agreeing with the server's.
       * The cost when it is a tenant's gate being opened is one identity refetch.
       */
      if (v.palette !== undefined || v.accent !== undefined || v.remoteControlEnabled !== undefined) {
        void qc.invalidateQueries({ queryKey: ['me'] });
      }
    },
  });
}

export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      organisationId: string;
      name: string;
      isDefault?: boolean;
      /* Recorded, not enforced — see `Department.status`. */
      status?: 'active' | 'suspended' | 'inactive';
    }) =>
      api.post<{ department: Department }>('/departments', body),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: tenancyKeys.departments(v.organisationId) }),
  });
}

/**
 * Delete a department — and be told what it did to the people who were in it.
 *
 * ── NOBODY LOSES ANYTHING, AND THE SCREEN HAS TO SAY SO ─────────────────────
 * `profiles.department_id` is `ON DELETE SET NULL`, so every member keeps their organisation,
 * their roles, their site grants, their alarms and their audit history and simply stops being in
 * a department. A department carries no isolation at all — deliberately, because a second axis
 * that could narrow what an operator sees is how a filter becomes a false all-clear — so losing
 * one can cost none.
 *
 * "NOT IN A DEPARTMENT" IS A REAL STATE. The column has been nullable since 0043 for exactly that
 * reason, and the Departments column already says so in its empty state. `releasedMembers` is on
 * the response so the console can put the number in front of the reader at the moment of the act
 * rather than leaving an administrator to find an empty column days later and wonder what broke.
 *
 * ── WHAT THE SERVER DOES NOT REFUSE, WHICH IS WORTH KNOWING BEFORE YOU HIDE A BUTTON ──
 * `deleteDepartment` refuses nothing about the DEFAULT one: it asserts `tenants.edit`, proves
 * reach, and deletes. So the control is drawn for the default too — and the form carries the one
 * extra consequence, which is that the organisation is then left with no default at all. That is
 * legal (`departmentForProfileOrThrow` resolves a null to a null) and it is not obvious, so the
 * confirmation says it rather than letting somebody discover it at the next invitation.
 *
 * INVALIDATES THE MEMBER LIST TOO. `GET /users` carries `departmentId` on every row, and this act
 * nulls it for everybody who was in here — a list left in cache would go on filing them under a
 * department that no longer exists.
 */
export function useDeleteDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; organisationId: string }) =>
      api.del<{ ok: true; releasedMembers: number; detail: string }>(`/departments/${id}`),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: tenancyKeys.departments(v.organisationId) });
      void qc.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      organisationId: string;
      name: string;
      permissions: string[];
      /* Omitted means `organisation` on the server, so a caller that has never heard of scoping
         authors exactly the role it used to. */
      scopeKind?: 'organisation' | 'site';
    }) => api.post<{ role: RoleDetail }>('/roles', body),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: tenancyKeys.roles(v.organisationId) }),
  });
}

/**
 * Change a role's permission set.
 *
 * The server bounds the request by the caller's OWN permissions — you cannot grant what you do
 * not hold. That check lives there and not here, because a client-side guard is a courtesy and
 * this one is a security boundary: it is the hole that compromised the Pro backend, where any
 * tenant administrator could assign themselves a platform-wide role.
 */
export function usePatchRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      orgId: _orgId,
      ...body
    }: {
      id: string;
      orgId: string;
      name?: string;
      permissions?: string[];
      /**
       * Switching a role to `site` does NOT grant anybody anything — it makes their grants start
       * being consulted, and somebody with none then reaches no sites at all. That is the honest
       * behaviour and the screen has to say so before the switch is thrown, because the
       * alternative default, treating no grants as the whole fleet, fails open.
       */
      scopeKind?: 'organisation' | 'site';
    }) => api.patch<{ role: RoleDetail }>(`/roles/${id}`, body),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: tenancyKeys.roles(v.orgId) }),
  });
}

/**
 * Take a copy of the default this role has been reading — and stop reading it.
 *
 * ── TWO ROUTES AND NOT A FIELD ON THE PATCH, WHICH IS THE SERVER'S SHAPE ─────
 * The strongest consequence on the roles screen is that you stop receiving every future change
 * to the default. Folding it into the PATCH would make that happen as a side effect of saving
 * permissions — while somebody was doing something else — which is the frozen fork 0063 exists
 * to end, re-created one save at a time. So it is its own press, with its own sentence beside it.
 *
 * ── IDEMPOTENT, BOTH OF THEM ────────────────────────────────────────────────
 * Customising a customised role and resetting a following one return the role unchanged and
 * write no audit row, so a double-click and two administrators pressing at once are both
 * harmless. Neither takes a body.
 *
 * INVALIDATES THE WHOLE CATALOGUE, not just this row: a role inside this organisation may be
 * FOLLOWED by one in a descendant, and the columns can be showing a descendant's list. Refetching
 * one id would leave the follower quoting a set it no longer resolves to.
 */
export function useCustomiseRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; orgId: string }) =>
      api.post<{ role: RoleDetail }>(`/roles/${id}/customise`, {}),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: tenancyKeys.roles(v.orgId) }),
  });
}

/**
 * Give the copy back and follow the default again.
 *
 * NOT gated by `roles.customise` on the server, deliberately — `resetRole` asserts `roles.manage`
 * alone. A tenant whose entitlement lapses keeps the roles it customised while it held it, and
 * must still be able to put one back; gating the retreat would strand them between a
 * customisation they may not edit and a default they may not return to.
 *
 * It DELETES the role's own grant rows. The audit entry carries the whole set that was thrown
 * away, dormant rows included, so the act can be undone by hand from the trail alone — which is
 * the only way back, and why the console asks before it presses.
 */
export function useResetRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; orgId: string }) =>
      api.post<{ role: RoleDetail }>(`/roles/${id}/reset`, {}),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: tenancyKeys.roles(v.orgId) }),
  });
}

/**
 * Delete a role outright.
 *
 * ── THE THREE REFUSALS, AND ONLY TWO OF THEM ARE THE CONSOLE'S TO PRE-EMPT ───
 *
 * `deleteRole` asserts `roles.manage` alone — the same "direction of travel" reasoning `resetRole`
 * carries: taking a role AWAY is never gated behind `roles.customise`, or a tenant whose
 * entitlement lapsed would be left unable to tidy up the roles it authored while it held it.
 * Then it refuses three things, and the screen's job is different for each:
 *
 *   A SHARED ROLE       `refuseIfShared` — a platform template, or any row with a null
 *                       organisation. Nobody may delete one, including us, because it is offered
 *                       to every organisation on the deployment. KNOWN HERE: it is exactly
 *                       `followState(role) === 'shared'`, so the control is absent rather than
 *                       refused.
 *   SOMEBODY HOLDS IT   the foreign key would refuse, and a cascade was rejected precisely
 *                       because it would strip people of the role instead of stopping. KNOWN
 *                       HERE for the members the roster can see, so the confirmation names them.
 *                       Not knowable for a REVOKED holder — `GET /users` drops revoked rows and
 *                       the server counts them anyway, since profiles are never deleted — so
 *                       that one arrives as the server's own sentence.
 *   A FOLLOWER (0063)   a role in a descendant organisation that reads this one. There is no
 *                       foreign key for it — a follow is derived — so nothing in the database
 *                       would refuse and the check is written by hand in the service. NOT
 *                       KNOWABLE HERE either way: it needs the role catalogue of every
 *                       descendant, and this screen holds one organisation's. It arrives as a 409
 *                       naming the organisations.
 *
 *                       SINCE 0068 IT CANNOT HAPPEN, and the reasoning is worth keeping rather
 *                       than deleting. A follow now resolves in one hop to the platform template,
 *                       so only a template is ever a source — and a template is refused by a
 *                       different guard long before this one. Every role this button can reach is
 *                       one the organisation authored itself, and nothing follows those. The
 *                       server still asks instead of assuming, because "always empty today" is a
 *                       fact about the rest of the system rather than about that function; the
 *                       console keeps surfacing whatever sentence comes back for the same reason.
 *
 * So the console hides the first, states the second, and passes the third through untouched.
 *
 * INVALIDATES THE ORGANISATION LIST AS WELL AS THE CATALOGUE, which `customise` and `reset` do
 * not need to: those two move what a role HOLDS, this one changes how many roles exist, and
 * `roleCount` is drawn on the Organisation screen straight off `GET /organisations`.
 *
 * Nothing else has to be cleaned up client-side: `role_permissions_own` goes with the row by
 * cascade, and the refusal above guarantees nobody was holding it, so no member row moved.
 */
export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; orgId: string }) => api.del<{ ok: true }>(`/roles/${id}`),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: tenancyKeys.roles(v.orgId) });
      void qc.invalidateQueries({ queryKey: tenancyKeys.orgs });
    },
  });
}
